import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import { useStore } from "vuex";
  import {
    Bed,
    Bath,
    Car,
    Maximize2,
    Sofa,
    Map,
    Lock,
    ChevronDown,
    Check,
    FileText,
    ArrowLeft,
    Loader2,
  } from "lucide-vue-next";
  import CustomerNavbar from "@/v2/components/CustomerNavbar.vue";
  import CustomerBottomNavigation from "@/v2/components/CustomerBottomNavigation.vue";
  import builderApi from "@/services/api/builderApi";
  import projectPageApi, {
    ProjectPageData,
  } from "@/services/api/projectPageApi";
  import inclusionApi from "@/services/api/inclusionApi";
  import colorSchemeApi from "@/services/api/colorSchemeApi";
  import optionalUpgradeApi from "@/services/api/optionalUpgradeApi";
  import { HouseDesignInterface } from "@/types/HouseDesignInterface";
  import { InclusionInterface } from "@/types/InclusionInterface";
  import { EstateColorSchemeInterface } from "@/types/ColorSchemeInterface";
  import {
    OptionalUpgrade,
    EstateOptionalUpgrade,
  } from "@/types/OptionalUpgradeInterface";
  import { InclusionCategory } from "@/enums/InclusionCategory";
  import { MaterialCategory } from "@/enums/MaterialCategory";
  import { LotStatus } from "@/enums/LotStatus";
  import { formatPrice } from "@/helpers/transformation/price";

  interface LotOption {
    _id: string;
    title: string;
    price?: number;
    hidePrice?: boolean;
    status?: string;
    compatibleFacadeIds: string[];
  }

  type BottomNavView =
    | "hero"
    | "browse"
    | "detail"
    | "eoi"
    | "eoi-success"
    | "map"
    | "resources";

  const defaultDescription =
    "Step inside a home designed to balance light, space, and warmth. An open-plan layout brings the kitchen, dining, and living zones together for effortless connection, while restful bedrooms offer the perfect retreat at the end of the day.";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'HouseInteriorsView',
  setup(__props, { expose: __expose }) {
  __expose();

  const route = useRoute();
  const router = useRouter();
  const store = useStore();

  const house = ref<HouseDesignInterface | null>(null);
  const projectPageData = ref<ProjectPageData | null>(null);
  const estateName = ref("");
  const lots = ref<LotOption[]>([]);
  const estateInclusions = ref<InclusionInterface[]>([]);
  const estateColorSchemes = ref<EstateColorSchemeInterface[]>([]);
  const estateOptionalUpgrades = ref<EstateOptionalUpgrade[]>([]);
  const selectedLotId = ref<string>("");
  const selectedFacadeId = ref<string>("");
  const heroImageWr = ref<HTMLElement | null>(null);
  const mobileBottomNavTrigger = ref<HTMLElement | null>(null);
  const showMobileBottomNavigation = ref(false);
  let mobileBottomNavObserver: IntersectionObserver | null = null;

  const sidebarView = ref<"summary" | "info-pack">("summary");
  const infoPackForm = reactive({
    fullName: "",
    email: "",
    phone: "",
    message: "",
  });
  const infoPackErrors = reactive({ fullName: "", email: "", phone: "" });
  const infoPackSubmitting = ref(false);
  const infoPackSubmitted = ref(false);
  const showFacadeConflictModal = ref(false);
  const isFloorplanOpen = ref(false);
  const pendingFacadeId = ref("");
  const selectedInternalSchemeId = ref<string>("");
  const selectedUpgradeIds = ref<string[]>([]);
  const activeRoomIndex = ref(0);
  const mobileSchemeRoomIndexes = ref<Record<string, number>>({});

  const estateId = computed(() => route.query.estateId as string | undefined);
  const isEmbedded = computed(() => route.query.embed === "true");
  const houseId = computed(() => route.params.houseId as string);
  const initialLotId = computed(() => route.query.lotId as string | undefined);
  const initialFacade = computed(
    () => route.query.facade as string | undefined
  );
  const estateSlug = computed(() => slugifyEstateTitle(estateName.value));

  const slugifyEstateTitle = (title?: string) => {
    if (!title) return "";

    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const fetchHouse = async () => {
    const response = await builderApi.getHouseById(houseId.value);

    house.value = response.data;
  };

  const fetchEstateInclusions = async () => {
    if (!estateId.value) return;

    try {
      estateInclusions.value = await inclusionApi.getInclusionsByEstateId(
        estateId.value
      );
    } catch (error) {
      console.error("Failed to fetch estate inclusions:", error);
      estateInclusions.value = [];
    }
  };

  const fetchEstateColorSchemes = async () => {
    if (!estateId.value) return;

    try {
      estateColorSchemes.value = await colorSchemeApi.getColorSchemesByEstateId(
        estateId.value
      );
    } catch (error) {
      console.error("Failed to fetch estate color schemes:", error);
      estateColorSchemes.value = [];
    }
  };

  const fetchEstateOptionalUpgrades = async () => {
    if (!estateId.value) return;

    try {
      estateOptionalUpgrades.value =
        await optionalUpgradeApi.getOptionalUpgradesByEstateId(estateId.value);
    } catch (error) {
      console.error("Failed to fetch estate optional upgrades:", error);
      estateOptionalUpgrades.value = [];
    }
  };

  const fetchProjectPageData = async () => {
    if (!estateId.value) return;

    try {
      const { data } = await projectPageApi.getByEstateIdPublic(estateId.value);

      projectPageData.value = data;
    } catch (err) {
      console.log("No project page data found:", err);
    }
  };

  const fetchEstateName = () => {
    if (!estateId.value) return;

    const estate = store.state.user.landownerEstates?.find(
      (e: any) => e._id === estateId.value
    );

    if (estate?.title) {
      estateName.value = estate.title;
    }
  };

  const fetchLots = async () => {
    if (!estateId.value) {
      lots.value = [];
      return;
    }

    const response = await builderApi.getLotsByEstateAndHouseDesign(
      estateId.value,
      houseId.value
    );

    lots.value = (response.data || []).map((lot: any) => {
      const houseEntry = (lot.builders || [])
        .flatMap((b: any) => b.housesIds || [])
        .find(
          (h: any) => String(h.houseId?._id ?? h.houseId) === houseId.value
        );

      return {
        _id: lot._id,
        title: lot.title,
        price: lot.price,
        hidePrice: lot.hidePrice,
        status: lot.status,
        compatibleFacadeIds: (houseEntry?.facadeIds || []).map(String),
      };
    });
  };

  const setDefaultSelections = () => {
    if (!house.value) return;

    if (!selectedFacadeId.value && house.value.facades?.length) {
      const matchedFacade = house.value.facades.find(
        (facade) =>
          String(facade._id) === initialFacade.value ||
          facade.name === initialFacade.value
      );

      if (matchedFacade) {
        selectedFacadeId.value = String(matchedFacade._id);
      }
    }

    if (!selectedLotId.value) {
      selectedLotId.value = initialLotId.value || "";
    }

    if (!selectedInternalSchemeId.value && allColorSchemes.value.length) {
      selectedInternalSchemeId.value = allColorSchemes.value[0]._id;
    }
  };

  const updateMobileViewportBottomOffset = () => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    if (!isEmbedded.value) {
      document.documentElement.style.removeProperty(
        "--lt-mobile-viewport-bottom"
      );
      return;
    }

    const viewport = window.visualViewport;
    const bottomOffset = viewport
      ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
      : 0;

    document.documentElement.style.setProperty(
      "--lt-mobile-viewport-bottom",
      `${Math.round(bottomOffset)}px`
    );
  };

  const observeMobileBottomNavigation = () => {
    if (!mobileBottomNavTrigger.value) return;

    if (!("IntersectionObserver" in window)) {
      showMobileBottomNavigation.value = true;
      return;
    }

    mobileBottomNavObserver = new IntersectionObserver(
      ([entry]) => {
        showMobileBottomNavigation.value = entry.isIntersecting;
      },
      { threshold: 0 }
    );
    mobileBottomNavObserver.observe(mobileBottomNavTrigger.value);
  };

  watch(isEmbedded, updateMobileViewportBottomOffset);

  onMounted(async () => {
    updateMobileViewportBottomOffset();
    window.visualViewport?.addEventListener(
      "resize",
      updateMobileViewportBottomOffset
    );
    window.visualViewport?.addEventListener(
      "scroll",
      updateMobileViewportBottomOffset
    );

    fetchEstateName();
    await Promise.all([
      fetchHouse(),
      fetchLots(),
      fetchEstateInclusions(),
      fetchEstateColorSchemes(),
      fetchEstateOptionalUpgrades(),
      fetchProjectPageData(),
    ]);
    setDefaultSelections();
    observeMobileBottomNavigation();
  });

  onUnmounted(() => {
    window.visualViewport?.removeEventListener(
      "resize",
      updateMobileViewportBottomOffset
    );
    window.visualViewport?.removeEventListener(
      "scroll",
      updateMobileViewportBottomOffset
    );
    mobileBottomNavObserver?.disconnect();
    document.documentElement.style.removeProperty(
      "--lt-mobile-viewport-bottom"
    );
  });

  const featureList = computed(() => {
    if (!house.value?.features?.length) {
      return [
        "Open plan living",
        "Built-in wardrobes",
        "Low maintenance finishes",
        "Energy efficient lighting",
      ];
    }

    return house.value.features;
  });

  const compatibleLots = computed(() => {
    return lots.value.filter((lot) => {
      if (lot.status !== LotStatus.AVAILABLE) return false;

      if (!selectedFacadeId.value) return true;

      if (!lot.compatibleFacadeIds.length) return true;

      return lot.compatibleFacadeIds.includes(selectedFacadeId.value);
    });
  });

  const hasAnyAvailableLots = computed(() =>
    lots.value.some((lot) => lot.status === LotStatus.AVAILABLE)
  );

  watch(selectedFacadeId, () => {
    if (!selectedLotId.value) return;

    const isStillCompatible = compatibleLots.value.some(
      (lot) => lot._id === selectedLotId.value
    );

    if (!isStillCompatible) selectedLotId.value = "";
  });

  watch(selectedInternalSchemeId, () => {
    activeRoomIndex.value = 0;
  });

  const selectedLot = computed(() =>
    lots.value.find((lot) => lot._id === selectedLotId.value)
  );
  const selectedLotTitle = computed(() =>
    selectedLot.value ? `Lot ${selectedLot.value.title}` : "this lot"
  );

  const basePrice = computed(() => {
    return house.value?.basePrice || 0;
  });

  const upgradesTotal = computed(() => {
    if (!allOptionalUpgrades.value.length) return 0;

    return allOptionalUpgrades.value
      .filter((upgrade) => selectedUpgradeIds.value.includes(upgrade._id))
      .reduce((sum, upgrade) => sum + (upgrade.price || 0), 0);
  });

  const displayPrice = computed(() =>
    formatPrice(basePrice.value + upgradesTotal.value)
  );

  const heroImage = computed(() => {
    const facade = selectedFacade.value;

    return (
      facade?.url ||
      house.value?.facades?.[0]?.url ||
      house.value?.mainImages?.[0] ||
      "/placeholder-house.jpg"
    );
  });

  const floorplanImage = computed(() => {
    return house.value?.floorplans?.[0]?.url || "/placeholder-floorplan.jpg";
  });

  const selectedFacade = computed(() => {
    return (
      house.value?.facades?.find(
        (facade) => String(facade._id) === selectedFacadeId.value
      ) || null
    );
  });

  const allColorSchemes = computed(() => {
    if (house.value?.internalColorSchemes?.length)
      return house.value.internalColorSchemes as any[];

    return estateColorSchemes.value;
  });

  const selectedInternalScheme = computed(
    () =>
      allColorSchemes.value.find(
        (s: any) => s._id === selectedInternalSchemeId.value
      ) || null
  );

  const allOptionalUpgrades = computed(
    (): Array<OptionalUpgrade | EstateOptionalUpgrade> => {
      const builderUpgrades = house.value?.optionalUpgrades || [];

      return [...builderUpgrades, ...estateOptionalUpgrades.value];
    }
  );

  const getSchemeRoomImages = (
    scheme: any
  ): Array<{ name: string; url: string }> => {
    if (!scheme) return [];

    if (scheme.roomImagesList?.length) {
      return scheme.roomImagesList
        .filter((r: any) => r.url)
        .map((r: any) => ({ name: r.name, url: r.url }));
    }

    const imgs: Array<{ name: string; url: string }> = [];

    if (scheme.roomImages?.livingRoom) {
      imgs.push({ name: "Living Room", url: scheme.roomImages.livingRoom });
    }

    if (scheme.roomImages?.kitchen) {
      imgs.push({ name: "Kitchen", url: scheme.roomImages.kitchen });
    }

    if (scheme.roomImages?.bathroom) {
      imgs.push({ name: "Bathroom", url: scheme.roomImages.bathroom });
    }

    if (!imgs.length) {
      const names = ["Living Room", "Kitchen", "Bathroom"];

      (scheme.imagesUrls || [])
        .slice(0, 3)
        .forEach((url: string, i: number) =>
          imgs.push({ name: names[i], url })
        );
    }

    return imgs;
  };

  const displayRoomImages = computed(() =>
    getSchemeRoomImages(selectedInternalScheme.value)
  );

  const mobileSchemeCards = computed(() =>
    allColorSchemes.value.map((scheme: any) => {
      const roomImages = getSchemeRoomImages(scheme);
      const savedIndex = mobileSchemeRoomIndexes.value[scheme._id] ?? 0;
      const activeIndex = Math.min(
        savedIndex,
        Math.max(roomImages.length - 1, 0)
      );

      return {
        scheme,
        roomImages,
        activeIndex,
        activeRoom: roomImages[activeIndex],
      };
    })
  );

  const inclusionTitleMap: Record<InclusionCategory, string> = {
    [InclusionCategory.INDOOR]: "Indoor Inclusions",
    [InclusionCategory.KITCHEN]: "Kitchen",
    [InclusionCategory.BATHROOM_LAUNDRY]: "Bathroom & Laundry",
    [InclusionCategory.OUTDOOR]: "Outdoor",
  };

  const inclusionSections = computed(() => {
    const sections: Array<{ key: string; title: string; items: string[] }> = [];

    if (house.value?.categorizedInclusions?.length) {
      for (const section of house.value.categorizedInclusions) {
        sections.push({
          key: section.category,
          title: inclusionTitleMap[section.category] || section.category,
          items: section.items,
        });
      }
    } else if (house.value?.inclusions?.length) {
      sections.push({
        key: "inclusions",
        title: "Inclusions",
        items: house.value.inclusions.map((item: any) => item.name),
      });
    }

    for (const pkg of estateInclusions.value) {
      if (pkg.categories?.length) {
        for (const cat of pkg.categories) {
          sections.push({
            key: `${pkg._id}-${cat.name}`,
            title: cat.name,
            items: cat.items.map((item) => item.name),
          });
        }
      } else if (pkg.inclusions?.length) {
        sections.push({
          key: pkg._id,
          title: pkg.type,
          items: pkg.inclusions.map((item) => item.name),
        });
      }
    }

    return sections;
  });

  const openInclusionKeys = ref<string[]>([]);

  const toggleInclusion = (key: string) => {
    openInclusionKeys.value = openInclusionKeys.value.includes(key)
      ? openInclusionKeys.value.filter((item) => item !== key)
      : [...openInclusionKeys.value, key];
  };

  const isInclusionOpen = (key: string) =>
    openInclusionKeys.value.includes(key);

  const materialLabels: Record<MaterialCategory, string> = {
    [MaterialCategory.FLOOR_TILES]: "Floor Tiles",
    [MaterialCategory.SPLASHBACK]: "Splashback",
    [MaterialCategory.BATH_TILES]: "Bath Tiles",
    [MaterialCategory.BENCHTOPS]: "Benchtops",
    [MaterialCategory.CABINETS]: "Cabinets",
    [MaterialCategory.CARPETS]: "Carpets",
    [MaterialCategory.PAINT]: "Paint",
  };

  const materialsByCategory = computed(() => {
    const scheme = selectedInternalScheme.value;

    if (!scheme) return [];

    if (scheme.includedMaterials?.length) {
      return scheme.includedMaterials.map((material: any, index: number) => {
        const category = material?.category;
        const name = material?.name || "Material";

        return {
          key: `${category || "material"}-${name}-${index}`,
          label:
            (category && materialLabels[category as MaterialCategory]) ||
            category ||
            "",
          name,
          details: material?.details || "",
          swatchStyle: material?.swatchImageUrl
            ? {
                backgroundImage: `url(${material.swatchImageUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }
            : { backgroundColor: "#f2f4f7" },
        };
      });
    }

    if (scheme.productMaterials?.length) {
      return scheme.productMaterials.map((material: any, index: number) => {
        const name = material?.name || "Material";

        return {
          key: `${name}-${index}`,
          label: "",
          name,
          details: material?.description || "",
          swatchStyle: material?.url
            ? {
                backgroundImage: `url(${material.url})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }
            : { backgroundColor: "#f2f4f7" },
        };
      });
    }

    return [];
  });

  const selectFacade = (facadeId: string) => {
    if (selectedLotId.value) {
      const currentLot = lots.value.find(
        (lot) => lot._id === selectedLotId.value
      );

      if (
        currentLot?.compatibleFacadeIds?.length &&
        !currentLot.compatibleFacadeIds.includes(facadeId)
      ) {
        pendingFacadeId.value = facadeId;
        showFacadeConflictModal.value = true;
        return;
      }
    }

    selectedFacadeId.value = facadeId;
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const keepLot = () => {
    showFacadeConflictModal.value = false;
    pendingFacadeId.value = "";
  };

  const confirmFacadeChange = () => {
    selectedFacadeId.value = pendingFacadeId.value;
    selectedLotId.value = "";
    showFacadeConflictModal.value = false;
    pendingFacadeId.value = "";
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const selectInternalScheme = (schemeId: string) => {
    selectedInternalSchemeId.value = schemeId;
  };

  const selectMobileSchemeRoom = (schemeId: string, roomIndex: number) => {
    mobileSchemeRoomIndexes.value = {
      ...mobileSchemeRoomIndexes.value,
      [schemeId]: roomIndex,
    };
  };

  const toggleUpgrade = (upgradeId: string) => {
    selectedUpgradeIds.value = selectedUpgradeIds.value.includes(upgradeId)
      ? selectedUpgradeIds.value.filter((id) => id !== upgradeId)
      : [...selectedUpgradeIds.value, upgradeId];
  };

  const openFloorplanPreview = () => {
    isFloorplanOpen.value = true;
  };

  const openMasterplan = () => {
    if (!estateId.value) return;

    router.push({
      name: "MasterplanViewV2",
      params: { estateId: estateId.value },
      query: {
        houseDesignId: houseId.value,
        ...(selectedFacadeId.value ? { facadeId: selectedFacadeId.value } : {}),
        ...(isEmbedded.value ? { embed: "true" } : {}),
      },
    });
  };

  const handleBottomNavigate = (view: BottomNavView) => {
    if (view === "browse") {
      router.push({
        name: "BrowseHomeDesignsV2",
        params: { estateId: estateId.value },
        query: isEmbedded.value ? { embed: "true" } : {},
      });
      return;
    }

    if (view === "map") {
      openMasterplan();
      return;
    }

    if (view === "resources" && estateSlug.value) {
      router.push({
        name: "PublicProjectPage",
        params: { slug: estateSlug.value },
      });
    }
  };

  const goBack = () => {
    router.back();
  };

  const formatArea = (area?: number) => {
    if (!area) return "0 m²";

    return `${area.toFixed(1)} m²`;
  };

  const formatUpgradeDescription = (
    upgrade: OptionalUpgrade | EstateOptionalUpgrade
  ) => {
    if ("description" in upgrade && upgrade.description) {
      return upgrade.description;
    }

    if ("includedItems" in upgrade && upgrade.includedItems?.length) {
      return upgrade.includedItems.join(", ");
    }

    return "";
  };

  const navigateToEOI = () => {
    if (!selectedLotId.value) return;

    router.push({
      name: "SubmitExpressionOfInterestV2",
      query: {
        houseId: houseId.value,
        estateId: estateId.value,
        facadeId: selectedFacadeId.value,
        colorSchemeId: selectedInternalSchemeId.value,
        lotId: selectedLotId.value,
        upgradeIds: selectedUpgradeIds.value.join(","),
        ...(isEmbedded.value ? { embed: "true" } : {}),
      },
    });
  };

  const submitInfoPack = async () => {
    infoPackErrors.fullName = infoPackForm.fullName.trim()
      ? ""
      : "Full name is required";
    infoPackErrors.email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
      infoPackForm.email.trim()
    )
      ? ""
      : "Valid email is required";
    infoPackErrors.phone = infoPackForm.phone.trim() ? "" : "Phone is required";

    if (infoPackErrors.fullName || infoPackErrors.email || infoPackErrors.phone)
      return;

    infoPackSubmitting.value = true;
    await new Promise((resolve) => setTimeout(resolve, 1200));
    infoPackSubmitting.value = false;
    infoPackSubmitted.value = true;
  };
  const resetInfoPack = () => {
    infoPackSubmitted.value = false;
    infoPackForm.fullName = "";
    infoPackForm.email = "";
    infoPackForm.phone = "";
    infoPackForm.message = "";
    infoPackErrors.fullName = "";
    infoPackErrors.email = "";
    infoPackErrors.phone = "";
    sidebarView.value = "summary";
  };

const __returned__ = { route, router, store, house, projectPageData, estateName, lots, estateInclusions, estateColorSchemes, estateOptionalUpgrades, selectedLotId, selectedFacadeId, heroImageWr, mobileBottomNavTrigger, showMobileBottomNavigation, get mobileBottomNavObserver() { return mobileBottomNavObserver }, set mobileBottomNavObserver(v) { mobileBottomNavObserver = v }, sidebarView, infoPackForm, infoPackErrors, infoPackSubmitting, infoPackSubmitted, showFacadeConflictModal, isFloorplanOpen, pendingFacadeId, selectedInternalSchemeId, selectedUpgradeIds, activeRoomIndex, mobileSchemeRoomIndexes, estateId, isEmbedded, houseId, initialLotId, initialFacade, estateSlug, defaultDescription, slugifyEstateTitle, fetchHouse, fetchEstateInclusions, fetchEstateColorSchemes, fetchEstateOptionalUpgrades, fetchProjectPageData, fetchEstateName, fetchLots, setDefaultSelections, updateMobileViewportBottomOffset, observeMobileBottomNavigation, featureList, compatibleLots, hasAnyAvailableLots, selectedLot, selectedLotTitle, basePrice, upgradesTotal, displayPrice, heroImage, floorplanImage, selectedFacade, allColorSchemes, selectedInternalScheme, allOptionalUpgrades, getSchemeRoomImages, displayRoomImages, mobileSchemeCards, inclusionTitleMap, inclusionSections, openInclusionKeys, toggleInclusion, isInclusionOpen, materialLabels, materialsByCategory, selectFacade, keepLot, confirmFacadeChange, selectInternalScheme, selectMobileSchemeRoom, toggleUpgrade, openFloorplanPreview, openMasterplan, handleBottomNavigate, goBack, formatArea, formatUpgradeDescription, navigateToEOI, submitInfoPack, resetInfoPack, computed, onMounted, onUnmounted, reactive, ref, watch, get useRoute() { return useRoute }, get useRouter() { return useRouter }, get useStore() { return useStore }, get Bed() { return Bed }, get Bath() { return Bath }, get Car() { return Car }, get Maximize2() { return Maximize2 }, get Sofa() { return Sofa }, get Map() { return Map }, get Lock() { return Lock }, get ChevronDown() { return ChevronDown }, get Check() { return Check }, get FileText() { return FileText }, get ArrowLeft() { return ArrowLeft }, get Loader2() { return Loader2 }, CustomerNavbar, CustomerBottomNavigation, get builderApi() { return builderApi }, get projectPageApi() { return projectPageApi }, get ProjectPageData() { return ProjectPageData }, get inclusionApi() { return inclusionApi }, get colorSchemeApi() { return colorSchemeApi }, get optionalUpgradeApi() { return optionalUpgradeApi }, get HouseDesignInterface() { return HouseDesignInterface }, get InclusionInterface() { return InclusionInterface }, get EstateColorSchemeInterface() { return EstateColorSchemeInterface }, get OptionalUpgrade() { return OptionalUpgrade }, get EstateOptionalUpgrade() { return EstateOptionalUpgrade }, get InclusionCategory() { return InclusionCategory }, get MaterialCategory() { return MaterialCategory }, get LotStatus() { return LotStatus }, get formatPrice() { return formatPrice } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})